import { useState, useEffect } from 'react';
import { IChatbotPopup } from 'interfaces/components/chatbot/chatbot';
import { addMessage, AutoCompletionsData } from 'utils/helpers/chatbotHelpers';
import { getCompassQuestions } from 'network/getCompassQuestions';
import { getCompassAutoCompletions } from 'network/getCompassAutoCompletions';
import { useAutocomplete } from 'hooks/useAutocomplete';
import { useChatMessages } from 'hooks/useChatMessages';
import { useModalInteractions } from 'hooks/useModalInteractions';
import ChatbotPopup from './ChatbotPopup';
import { RateLimitError } from 'utils/helpers/chatbotHelpers';

const AUTOCOMPLETE_DEBOUNCE_MS = 300;

const ChatbotLogic = (props: IChatbotPopup) => {
  const [inputValue, setInputValue] = useState('');
  const [autocompletions, setAutocompletions] = useState<AutoCompletionsData | null>(null);
  const { isOpen, onClose, messages, setMessages, chatbotData, setChatbotData } = props;

  const {
    showSuggestions,
    focusedIndex,
    handleInputChange: handleAutocompleteInputChange,
    handleKeyDown,
    handleSuggestionClick: handleAutocompleteSuggestionClick,
    resetAutocomplete
  } = useAutocomplete(autocompletions);

  const { isLoading, sendMessage } = useChatMessages(
    messages,
    setMessages
  );

  const { chatAreaRef, popupRef } = useModalInteractions(
    isOpen,
    onClose,
    messages,
    resetAutocomplete
  );

  useEffect(() => {
    if (isOpen) {
      setInputValue('');
      resetAutocomplete();
    } else {
      resetAutocomplete();
    }
  }, [isOpen, resetAutocomplete]);

  useEffect(() => {
    const loadChatbotDataOnOpen = async () => {
      if (!isOpen || chatbotData) return;

      const loadingMessage = addMessage('', false, true);
      setMessages(prev => [...prev, loadingMessage]);

      try {
        const compassData = await getCompassQuestions();
        setChatbotData(compassData);
      } catch (error) {
        setMessages(prev => prev.filter(msg => msg.id !== loadingMessage.id));
        const errorMessage = addMessage(
          'Sorry, I could not load the chatbot data. Please try again.',
          false
        );
        setMessages(prev => [...prev, errorMessage]);
        return;
      }

      setMessages(prev => prev.filter(msg => msg.id !== loadingMessage.id));
    };

    loadChatbotDataOnOpen();
  }, [isOpen, chatbotData, setMessages, setChatbotData]);

  useEffect(() => {
    if (!isOpen) return;

    const trimmed = inputValue.trim();
    if (trimmed.length === 0) {
      setAutocompletions(null);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        const data = await getCompassAutoCompletions(trimmed);
        setAutocompletions(data);
      } catch (error) {
        if (error instanceof RateLimitError) {
          const errorMessage = addMessage(error.message, false);
          setMessages(prev => [...prev, errorMessage]);
        } else {
          console.warn('Autocomplete request failed:', error);
        }
      }
    }, AUTOCOMPLETE_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [inputValue, isOpen, setMessages]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    handleAutocompleteInputChange(value);
  };

  const handleSendMessage = async () => {
    const messageToSend = inputValue.trim();
    if (!messageToSend) return;
    setInputValue('');
    resetAutocomplete();
    try {
      await sendMessage(messageToSend);
    } catch (error) {
      if (error instanceof RateLimitError) {
        const errorMessage = addMessage(error.message, false);
        setMessages(prev => [...prev, errorMessage]);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleKeyboardNavigation = (e: React.KeyboardEvent) => {
    handleKeyDown(e, inputValue, handleSendMessage, suggestion => {
      setInputValue(suggestion);
    });
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleAutocompleteSuggestionClick(suggestion, selectedSuggestion => {
      setInputValue(selectedSuggestion);
    });
  };

  const handleSuggestedQuestionClick = (question: string) => {
    setInputValue('');
    resetAutocomplete();
    sendMessage(question);
  };

  return (
    <ChatbotPopup
      {...props}
      inputValue={inputValue}
      isLoading={isLoading}
      showSuggestions={showSuggestions}
      focusedIndex={focusedIndex}
      chatAreaRef={chatAreaRef}
      popupRef={popupRef}
      chatbotData={chatbotData}
      autocompletions={autocompletions}
      handleInputChange={handleInputChange}
      handleSendMessage={handleSendMessage}
      handleKeyPress={handleKeyPress}
      handleKeyDown={handleKeyboardNavigation}
      handleSuggestionClick={handleSuggestionClick}
      handleSuggestedQuestionClick={handleSuggestedQuestionClick}
    />
  );
};

export default ChatbotLogic;
